<script lang="ts">
	import { format } from 'svelte-i18n';

	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';

	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { devices } from 'src/stores/lists/devices';
	import { devices_settings } from 'src/stores/device-runtime/status';

	import { Button, Text, Input } from '@Shelly/shelly-design-system';
	import { mathNormalize, valueOutOfRange } from 'src/helpers/utilities';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { round } from 'src/helpers/units_helpers';
	import {
		getBleTemperatureOffsetRange,
		isBleTemperatureOffsetFahrenheit,
	} from 'src/helpers/ble_settings_helpers';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import DynamicAlert from '../DynamicAlert.svelte';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		real_id,
	} from 'src/helpers/interface_helpers';

	export let device;
	export let setting: 'offset' | 'threshold';
	export let type: 'temperature' | 'humidity' | 'utc' | 'bright' | 'dark' | 'smoke';
	export let min: number;
	export let max: number;

	let hadSession = false;
	let value: any = 0;
	let rawMin = -32768;
	let rawMax = 32767;
	let rawValue = 0;
	let command = '';
	let title = '';
	let param = '';
	let unit = '';
	let ariaLabel = '';
	let step = 1;

	const initDevice = $devices[device?.id] ?? $devices[real_id(device?.id)] ?? device;
	const initSettings = $devices_settings[real_id(initDevice?.id ?? device?.id)];
	$: liveDevice = $devices[device?.id] ?? $devices[real_id(device?.id)] ?? device;
	$: liveSettings = $devices_settings[real_id(liveDevice?.id ?? device?.id)];

	const usesFahrenheit = (d = liveDevice) => isBleTemperatureOffsetFahrenheit(d, liveSettings);

	let prevUnit = isBleTemperatureOffsetFahrenheit(initDevice, initSettings);

	/** Keep the °C/°F chrome in sync with the unit actually used for this device. */
	const applyTemperatureChrome = (useFahrenheit: boolean) => {
		unit = useFahrenheit ? '°F' : '°C';
		({ min, max } = getBleTemperatureOffsetRange(useFahrenheit));
		title = $format('text_sensor_settings_temperature_new', {
			default: 'Offset in {unit} to be applied to the measured temperature.',
			values: { unit },
		});
		ariaLabel = $format('input_temperature_offset', {
			default: 'Enter the temperature offset. Value must be between {min} — {max}!',
			values: { min, max },
		});
	};

	/** Recompute the editable value/rawValue from the given device snapshot. */
	const applyDeviceValue = (d) => {
		if (setting === 'offset') {
			if (type === 'temperature') {
				rawValue = d?.temperatureOffset || 0;
				let next = round(rawValue * step);
				if (usesFahrenheit(d) && Number(next) !== 0) {
					next = round(celsius_to_fahrenheit(Number(next)) - celsius_to_fahrenheit(0));
				}
				value = next;
			} else if (type === 'humidity') {
				rawValue = d?.humidityOffset || 0;
				value = Math.round(rawValue * step);
			} else if (type === 'utc') {
				value = d?.utcOffset || 0;
			}
		} else if (setting === 'threshold') {
			if (type === 'bright') value = d?.brightThreshold || 500;
			else if (type === 'dark') value = d?.darkThreshold || 50;
			else if (type === 'smoke') value = d?.smokeThreshold || 30;
			else if (type === 'temperature') value = d?.temperatureThreshold || 50;
		}
	};

	if (setting === 'offset') {
		if (type === 'temperature') {
			command = 'setTemperatureOffset';
			param = 'temperatureOffset';
			step = 0.1;
			applyTemperatureChrome(prevUnit);
		} else if (type === 'humidity') {
			command = 'setHumidityOffset';
			param = 'humidityOffset';
			rawValue = device?.humidityOffset || 0;
			value = Math.round(rawValue * step);
			unit = '%';
			title = $format('text_sensor_settings_humidity', {
				default: 'Humidity offset in %. Value is applied to measured humidity.',
			});
			ariaLabel = $format('input_humidity_offset', {
				default: 'Enter the humidity offset. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'utc') {
			command = 'setUtcOffset';
			param = 'utcOffset';
			value = device?.utcOffset || 0;
			unit = $format('timer_text_min', {
				default: 'min',
			});
			title = $format('utc_offset_desctription', {
				default:
					"Set the device's time offset according to its timezone. In rare cases, it may differ from the account's.",
			});
			ariaLabel = $format('input_utc_offset', {
				default: 'Enter the UTC offset. Value must be between {min} — {max}!',
				values: { min, max },
			});
		}
	} else if (setting === 'threshold') {
		if (type === 'bright') {
			command = 'setBrightThreshold';
			param = 'brightThreshold';
			value = device?.brightThreshold || 500;
			title = $format('bright_threshold_description', {
				default: 'Illuminance values above this threshold will be considered "bright"',
			});
			ariaLabel = $format('input_bright_threshold', {
				default: 'Enter the bright threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'dark') {
			command = 'setDarkThreshold';
			param = 'darkThreshold';
			value = device?.darkThreshold || 50;
			title = $format('dark_threshold_description', {
				default: 'Illuminance values below this threshold will be considered "dark"',
			});
			ariaLabel = $format('input_dark_threshold', {
				default: 'Enter the dark threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'smoke') {
			command = 'setSmokeThreshold';
			param = 'smokeThreshold';
			value = device?.smokeThreshold || 30;
			title = $format('smoke_threshold_description', {
				default: 'Set smoke sensitivity, in %/m',
			});
			ariaLabel = $format('input_smoke_threshold', {
				default: 'Enter the smoke threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'temperature') {
			command = 'setTemperatureThreshold';
			param = 'temperatureThreshold';
			value = device?.temperatureThreshold || 50;
			title = $format('temperature_threshold_description', {
				default: 'Set temperature threshold, in °C',
			});
			unit = '°C';
			ariaLabel = $format('input_temperature_threshold', {
				default: 'Enter the temperature threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		}
	}

	// Seed the initial value from the live device (step/unit are already set above).
	applyDeviceValue(initDevice);

	let prevStatus;
	$: {
		const status = $controlBleDevice.currentStatusIndex;
		if (status === BLU_STATUS.DEVICE_PROCESSED && status !== prevStatus && liveDevice) {
			applyDeviceValue(liveDevice);
		}
		prevStatus = status;
	}

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const onSave = () => {
		if ($controlBleDevice.connectionState === 2) {
			let realValue = ['temperature', 'humidity'].includes(type) ? rawValue : value;

			if (
				setting === 'offset' &&
				type === 'temperature' &&
				Number(realValue) !== 0 &&
				usesFahrenheit(liveDevice)
			) {
				const celsiusOffset = fahrenheit_to_celsius(value) - fahrenheit_to_celsius(0);
				realValue = Math.round(celsiusOffset / step);
			}

			$os === 'IOS'
				? control_bluetooth_general_ios(device.id, command, param, realValue)
				: control_bluetooth_general_android(device.id, command, param, realValue);

			persistBleDeviceSetting(device, { [param]: realValue });
			goBackToSettingsList?.();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	$: if (liveDevice && setting === 'offset' && type === 'temperature') {
		const useFahrenheit = usesFahrenheit(liveDevice);
		if (useFahrenheit !== prevUnit) {
			prevUnit = useFahrenheit;
			applyTemperatureChrome(useFahrenheit);
			applyDeviceValue(liveDevice);
		}
	}

	$: error = valueOutOfRange(value, min, max)
		? $format('error_out_of_range', {
				default: `Value ${value} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: {
					value,
					unit,
					min,
					max,
				},
			})
		: '';

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: onSave,
				loading: false,
				disabled: controlsDisabled || !!error,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text styleType="body-small" class="mb-s">{title}</Text>

<Input
	aria-label={ariaLabel}
	{...getNumberInputConfig()}
	icon="fas fa-calculator"
	class="mb-m"
	{value}
	{step}
	{min}
	{max}
	disabled={controlsDisabled}
	on:input={(e) => {
		value = e.target['value'];
		if (['temperature', 'humidity'].includes(type)) {
			rawValue = mathNormalize(Math.round(+value / step), rawMin, rawMax);
		}
	}}
	info={$format('min_max_error_description', {
		default: 'Values must be between {min} — {max}.',
		values:
			type === 'temperature'
				? { min: min + unit, max: max + unit }
				: type === 'humidity'
					? { min: min + '%', max: max + '%' }
					: { min, max },
	})}
	{error}
	id="input-i9c9nc2"
/>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled || !!error} size="m" width="full" on:click={onSave}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
